import { useTheme } from '@emotion/react'
import styled from '@emotion/styled'
import { useRouter } from 'found'
import { useTranslation } from 'react-i18next'

import LongArrowLeft from '../../icons/LongArrowLeft'
import { useTypedSelector } from '../../index'
import {
  buildPunctualityPath,
  IntervalQuery,
  resolveIntervalFromQuery,
} from '../../lib/wagonQueryParams'
import { getCurrentTrainParts, TrainPart, TrainPartVehicle } from '../../reducers/wagonSlice'
import { getColor } from '../../Theme'

interface DepartedVehicle {
  vehicle: TrainPartVehicle
  from: string
  to: string
}

const WagonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 10px;
  margin-bottom: 80px;
`

const Row = styled.div<{ isComingTrain: boolean; isLeavingTrain: boolean }>`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 16px;
  background: ${(p) => getColor(p.theme, ['white'], ['black'])};
  border: 1px solid ${(p) => getColor(p.theme, ['grayLight'], ['white'])};
  border-radius: 15px;
  box-shadow: 0 2px 3px ${(p) => getColor(p.theme, ['grayDark'], ['transparent'])};
  color: ${(p) =>
    p.isComingTrain
      ? getColor(p.theme, ['darkGreen'], ['mediumGreen'])
      : p.isLeavingTrain
      ? getColor(p.theme, ['red'], ['red'])
      : getColor(p.theme, ['black'], ['white'])};
`
const LegendRow = styled.div`
  font-size: 15px;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 8px 16px;
  color: ${(p) => getColor(p.theme, ['black'], ['grayPink'])};
`
const DeletedWagonRow = styled.div`
  font-size: 15px;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 8px 16px;
  color: ${(p) => getColor(p.theme, ['grayDark'], ['grayPink'])};
`

const WagonInfo = styled.div`
  flex: 0 0 55px;
  text-align: left;
  font-weight: 500;
`

const TypeInfo = styled.div`
  flex: 0 0 60px;
  text-align: left;
`

const TrainInfo = styled.div`
  flex: 1;
  text-align: center;
`

const Arrow = styled.div`
  font-size: 50px;
  flex: 0 0 40px;
  text-align: center;
  justify-content: center;
`
const Interval = styled.div`
  flex: 1;
  text-align: center;
`

const DepartedWagonsHeader = styled.h4`
  color: ${(p) => getColor(p.theme, ['black'], ['grayPink'])};
  text-transform: uppercase;
  text-align: center;
  font-weight: normal;
  font-size: 14px;
  margin-top: 30px;
`

const TrainLink = styled.button`
  background: none;
  border: none;
  color: ${(p) => getColor(p.theme, ['primaryBlue'], ['white'])};
  text-decoration: underline;
  cursor: pointer;
  padding: 0;
  font: inherit;

  &:hover {
    text-decoration: none;
  }
`

interface ExistingVehicleProps {
  vehicle: TrainPartVehicle
  trainParts: TrainPart[]
  currentTrainPart: TrainPart
  theme: ReturnType<typeof useTheme>
  handleTrainClick: (trainNumber: string, businessDate: string, query?: IntervalQuery) => void
}

const ExistingVehicle = ({
  vehicle,
  trainParts,
  currentTrainPart,
  theme,
  handleTrainClick,
}: ExistingVehicleProps) => {
  if (!vehicle.assignedVehicle) {
    return null
  }

  const previous = vehicle.previousTrainInstance
  const next = vehicle.nextTrainInstance

  const fromTrain = previous?.isDifferentTrain ? previous.trainNumber : '-'
  const toTrain = next?.isDifferentTrain ? next.trainNumber : '-'

  const isFirstInterval = trainParts.indexOf(currentTrainPart) === 0
  const isLastInterval = trainParts.indexOf(currentTrainPart) === trainParts.length - 1

  const isComingTrain = !isFirstInterval && (previous?.isDifferentTrain || false)
  const isLeavingTrain = !isLastInterval && (next?.isDifferentTrain || false)

  const arrowColor = isLeavingTrain
    ? getColor(theme, ['red'], ['red'])
    : isComingTrain
    ? getColor(theme, ['darkGreen'], ['mediumGreen'])
    : getColor(theme, ['nightBlack'], ['white'])

  return (
    <Row isComingTrain={isComingTrain} isLeavingTrain={isLeavingTrain}>
      <WagonInfo>{vehicle.assignedVehicle.vehicleNumber || 'N/A'}</WagonInfo>
      <TypeInfo>{vehicle.assignedVehicle.type || 'N/A'}</TypeInfo>
      <TrainInfo>
        {fromTrain === '-' ? (
          '-'
        ) : previous ? (
          <TrainLink
            onClick={() =>
              handleTrainClick(fromTrain, previous.businessDate, {
                station: currentTrainPart.departureInfraPoint.shortCode,
                direction: 'arrive',
              })
            }
          >
            {fromTrain}
          </TrainLink>
        ) : (
          'N/A'
        )}
      </TrainInfo>
      <Arrow>
        <LongArrowLeft iconSize="default" fillColor={arrowColor} />
      </Arrow>
      <TrainInfo>
        {toTrain === '-' ? (
          '-'
        ) : next ? (
          <TrainLink
            onClick={() =>
              handleTrainClick(toTrain, next.businessDate, {
                station: currentTrainPart.arrivalInfraPoint.shortCode,
                direction: 'leave',
              })
            }
          >
            {toTrain}
          </TrainLink>
        ) : (
          'N/A'
        )}
      </TrainInfo>
    </Row>
  )
}

interface DepartedVehicleProps {
  departedVehicle: DepartedVehicle
  handleTrainClick: (trainNumber: string, businessDate: string, query?: IntervalQuery) => void
}

const DepartedVehicleComponent = ({ departedVehicle, handleTrainClick }: DepartedVehicleProps) => {
  if (!departedVehicle.vehicle.assignedVehicle) {
    return null
  }

  const next = departedVehicle.vehicle.nextTrainInstance

  return (
    <DeletedWagonRow>
      <WagonInfo>{departedVehicle.vehicle.assignedVehicle.vehicleNumber || 'N/A'}</WagonInfo>
      <TypeInfo>{departedVehicle.vehicle.assignedVehicle.type || 'N/A'}</TypeInfo>
      <Interval>
        {departedVehicle.from}-{departedVehicle.to}
      </Interval>
      <Interval>
        {next ? (
          <TrainLink
            onClick={() =>
              handleTrainClick(next.trainNumber, next.businessDate, {
                station: departedVehicle.to,
                direction: 'leave',
              })
            }
          >
            {next.trainNumber}
          </TrainLink>
        ) : (
          'N/A'
        )}
      </Interval>
    </DeletedWagonRow>
  )
}

const WagonRows = () => {
  const { t } = useTranslation()
  const theme = useTheme()
  const { router } = useRouter()
  const search = useTypedSelector((state) => state.found.match.location.search)
  const currentTrainNumber = useTypedSelector((state) => state.punctuality.selectedTrainNumber)
  const currentTrainDate = useTypedSelector((state) => state.punctuality.selectedTrainDate)
  const trainParts = useTypedSelector(getCurrentTrainParts)
  const resolvedInterval = resolveIntervalFromQuery(search, trainParts)

  if (resolvedInterval === null || !trainParts || trainParts.length === 0) {
    return (
      <WagonContainer>
        <div style={{ textAlign: 'center', padding: '20px' }}>{t('wagons.noDataForTrain')}</div>
      </WagonContainer>
    )
  }

  const currentTrainPart = trainParts[resolvedInterval]

  const handleTrainClick = (trainNumber: string, businessDate: string, query?: IntervalQuery) => {
    const previousTrain = currentTrainNumber
      ? { trainNumber: currentTrainNumber, trainDate: currentTrainDate }
      : undefined
    const nextPath = buildPunctualityPath(businessDate, trainNumber, query)
    const nextUrl = new URL(nextPath, window.location.origin)
    router.push({
      pathname: nextUrl.pathname,
      search: nextUrl.search,
      state: { previousTrain },
    })
  }

  const findDepartedVehicles: DepartedVehicle[] = currentTrainPart
    ? trainParts.slice(0, trainParts.indexOf(currentTrainPart)).flatMap((trainPart) =>
        trainPart.vehicles
          .filter((vehicle) => vehicle.nextTrainInstance?.isDifferentTrain)
          .map((vehicle) => ({
            vehicle,
            from: trainPart.departureInfraPoint.shortCode,
            to: trainPart.arrivalInfraPoint.shortCode,
          }))
      )
    : []

  return (
    <WagonContainer>
      <LegendRow key="legends">
        <WagonInfo>#</WagonInfo>
        <TypeInfo>{t('wagons.type')}</TypeInfo>
        <TrainInfo>{t('wagons.fromTrain')}</TrainInfo>
        <Arrow />
        <TrainInfo>{t('wagons.toTrain')}</TrainInfo>
      </LegendRow>
      {currentTrainPart?.vehicles.map((vehicle, index) => (
        <ExistingVehicle
          key={vehicle.assignedVehicle?.vehicleNumber || `unknown-${index}`}
          vehicle={vehicle}
          trainParts={trainParts}
          currentTrainPart={currentTrainPart}
          theme={theme}
          handleTrainClick={handleTrainClick}
        />
      ))}
      {findDepartedVehicles.length > 0 && (
        <div>
          <DepartedWagonsHeader>{t('wagons.departedWagons')}</DepartedWagonsHeader>
          <LegendRow key="leaved-wagons">
            <WagonInfo>#</WagonInfo>
            <TypeInfo>{t('wagons.type')}</TypeInfo>
            <Interval>{t('wagons.interval')}</Interval>
            <Interval>{t('wagons.toTrain')}</Interval>
          </LegendRow>
          {findDepartedVehicles.map((departedVehicle, index) => (
            <DepartedVehicleComponent
              key={
                departedVehicle.vehicle.assignedVehicle?.vehicleNumber ||
                `unknown-departed-${index}`
              }
              departedVehicle={departedVehicle}
              handleTrainClick={handleTrainClick}
            />
          ))}
        </div>
      )}
    </WagonContainer>
  )
}

export default WagonRows
